import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Trash2, Eye, Download, Mail, Send, Loader2, Check, Link2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { invoicesApi, invoiceTemplatesApi, clientsApi, taxMasterApi, feesMasterApi, agencyConfigApi, invoiceSettingsApi, productMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import PaymentLinkDialog from '@/components/PaymentLinkDialog'
import type { CreateInvoiceRequest, LineItemRequest, InvoiceTemplate, AdditionalContact, BusinessResponse, TaxMasterListItem, FeesMasterListItem } from '@/types'
import { formatCurrency, formatDateForInput } from '@/lib/utils'

// List of Indian Union Territories
const UNION_TERRITORIES = [
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
]

// Helper function to check if a state is a Union Territory
const isUnionTerritory = (state: string | undefined): boolean => {
  if (!state) return false
  return UNION_TERRITORIES.some(ut => ut.toLowerCase() === state.toLowerCase())
}

interface CreateInvoicePageProps {
  mode?: 'create' | 'view' | 'edit'
}

export default function CreateInvoicePage({ mode: propMode }: CreateInvoicePageProps = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const isViewMode = propMode === 'view' || (!!id && propMode !== 'edit')
  const isEditMode = propMode === 'edit'
  const isCreateMode = !id && !propMode

  // Get client_id from URL query params (for chatbot integration)
  const preselectedClientId = searchParams.get('client_id')

  // Fetch templates (INVOICE type only)
  const { data: templates = [] } = useQuery({
    queryKey: ['billingTemplates', 'INVOICE'],
    queryFn: () => invoiceTemplatesApi.getAll('INVOICE'),
  })

  // Fetch default template (INVOICE type)
  const { data: defaultTemplate } = useQuery({
    queryKey: ['billingTemplates', 'INVOICE', 'default'],
    queryFn: () => invoiceTemplatesApi.getDefault('INVOICE'),
  })

  // Fetch clients (basic list)
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.getAllLegacy,
  })

  // Fetch active taxes
  const { data: taxes = [] } = useQuery({
    queryKey: ['taxes', 'active'],
    queryFn: () => taxMasterApi.getAll(true),
  })

  // Fetch active fees
  const { data: feesList = [] } = useQuery({
    queryKey: ['fees', 'active'],
    queryFn: () => feesMasterApi.getAll(true),
  })

  // Fetch active products
  const { data: products = [] } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: () => productMasterApi.getAll(true),
  })

  // Fetch agency configuration for state comparison
  const { data: agencyConfig } = useQuery({
    queryKey: ['agencyConfig'],
    queryFn: agencyConfigApi.get,
  })

  // Fetch next invoice number preview (only in create mode)
  const { data: nextInvoiceNumber } = useQuery({
    queryKey: ['nextInvoiceNumber'],
    queryFn: invoiceSettingsApi.getNextNumber,
    enabled: isCreateMode,
  })

  // Fetch existing invoice if editing/viewing
  const { data: existingInvoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.getById(id!),
    enabled: !!id,
  })

  const [formData, setFormData] = useState<CreateInvoiceRequest>({
    client_id: '',
    due_date: formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()),
    currency: 'USD',
    discount_value: 0,
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
  })

  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null)
  const [selectedTax, setSelectedTax] = useState<TaxMasterListItem | null>(null)
  const [selectedFees, setSelectedFees] = useState<FeesMasterListItem | null>(null)

  // Determine the client ID to use (from form data or existing invoice)
  const clientIdToFetch = formData.client_id || existingInvoice?.client_id || ''

  // Fetch full client details (with additional contacts and businesses) when client is selected
  const { data: selectedClientDetails } = useQuery({
    queryKey: ['client', clientIdToFetch],
    queryFn: () => clientsApi.getById(clientIdToFetch),
    enabled: !!clientIdToFetch,
  })

  // Send invoice dialog state
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false)
  const [selectedEmails, setSelectedEmails] = useState<string[]>([])
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [isSendingInvoice, setIsSendingInvoice] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [isPaymentLinkDialogOpen, setIsPaymentLinkDialogOpen] = useState(false)

  // Set default template when loaded
  useEffect(() => {
    if (defaultTemplate && !selectedTemplate && isCreateMode) {
      setSelectedTemplate(defaultTemplate)
      setFormData(prev => ({
        ...prev,
        template_id: defaultTemplate.template_id,
        notes: defaultTemplate.default_notes || prev.notes,
        payment_terms: defaultTemplate.default_payment_terms || prev.payment_terms,
      }))
    }
  }, [defaultTemplate, selectedTemplate, isCreateMode])

  // Set next invoice number when loaded (only in create mode)
  useEffect(() => {
    if (nextInvoiceNumber?.next_invoice_number && isCreateMode && !formData.invoice_number) {
      setFormData(prev => ({
        ...prev,
        invoice_number: nextInvoiceNumber.next_invoice_number,
      }))
    }
  }, [nextInvoiceNumber, isCreateMode])

  // Set preselected client from URL query params (for chatbot integration)
  useEffect(() => {
    if (preselectedClientId && isCreateMode && clients.length > 0) {
      const client = clients.find(c => c.client_id === preselectedClientId)
      if (client) {
        setFormData(prev => ({
          ...prev,
          client_id: preselectedClientId,
          currency: client.currency || prev.currency,
        }))
      }
    }
  }, [preselectedClientId, isCreateMode, clients])

  // Populate form with existing invoice data
  useEffect(() => {
    if (existingInvoice) {
      setFormData({
        template_id: existingInvoice.template_id,
        client_id: existingInvoice.client_id,
        invoice_date: formatDateForInput(existingInvoice.invoice_date),
        due_date: formatDateForInput(existingInvoice.due_date),
        currency: existingInvoice.currency,
        tax_id: existingInvoice.tax_id,
        tax_rate: existingInvoice.tax_rate,
        is_gst_invoice: existingInvoice.is_gst_invoice,
        cgst_amount: existingInvoice.cgst_amount,
        sgst_amount: existingInvoice.sgst_amount,
        igst_amount: existingInvoice.igst_amount,
        utgst_amount: existingInvoice.utgst_amount,
        fees_id: existingInvoice.fees_id,
        fees_amount: existingInvoice.fees_amount,
        discount_type: existingInvoice.discount_type,
        discount_value: existingInvoice.discount_value,
        notes: existingInvoice.notes,
        payment_terms: existingInvoice.payment_terms,
        line_items: existingInvoice.line_items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit: item.unit,
          tax_rate: item.tax_rate,
          discount_amount: item.discount_amount,
          sort_order: item.sort_order,
        })),
      })
      if (existingInvoice.template) {
        setSelectedTemplate(existingInvoice.template)
      }
      // Set selected tax from existing invoice
      if (existingInvoice.tax_id && taxes.length > 0) {
        const tax = taxes.find(t => t.tax_id === existingInvoice.tax_id)
        if (tax) {
          setSelectedTax(tax)
        }
      }
      // Set selected fees from existing invoice
      if (existingInvoice.fees_id && feesList.length > 0) {
        const fees = feesList.find(f => f.fees_id === existingInvoice.fees_id)
        if (fees) {
          setSelectedFees(fees)
        }
      }
    }
  }, [existingInvoice, taxes, feesList])

  // Get selected client (basic info for display)
  const selectedClient = useMemo(() => {
    return clients.find(c => c.client_id === formData.client_id)
  }, [clients, formData.client_id])

  // Collect all available emails from client and businesses (using full client details)
  const availableEmails = useMemo(() => {
    if (!selectedClientDetails) return []

    const emails: { email: string; label: string; source: 'client' | 'business' | 'contact' }[] = []

    // Primary client email
    if (selectedClientDetails.client_email) {
      emails.push({
        email: selectedClientDetails.client_email,
        label: `${selectedClientDetails.client_name} (Primary)`,
        source: 'client'
      })
    }

    // Additional contacts (emails only)
    if (selectedClientDetails.additional_contacts) {
      selectedClientDetails.additional_contacts
        .filter((contact: AdditionalContact) => contact.contact_type === 'EMAIL')
        .forEach((contact: AdditionalContact) => {
          emails.push({
            email: contact.contact_value,
            label: contact.contact_label || `Additional Email`,
            source: 'contact'
          })
        })
    }

    // Business emails
    if (selectedClientDetails.businesses) {
      selectedClientDetails.businesses.forEach((business: BusinessResponse) => {
        if (business.business_email) {
          emails.push({
            email: business.business_email,
            label: `${business.business_name}`,
            source: 'business'
          })
        }
      })
    }

    return emails
  }, [selectedClientDetails])

  // Calculate totals with GST and Fees logic
  const calculations = useMemo(() => {
    const subtotal = formData.line_items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price)
    }, 0)
    const discountAmount = formData.discount_value || 0
    const taxableAmount = subtotal - discountAmount

    let taxAmount = 0
    let cgstAmount = 0
    let sgstAmount = 0
    let igstAmount = 0
    let utgstAmount = 0
    let isGSTInvoice = false
    let taxDescription = ''

    if (selectedTax) {
      if (selectedTax.is_gst) {
        isGSTInvoice = true
        const agencyState = agencyConfig?.state
        const clientState = selectedClientDetails?.state

        // Determine which GST components to apply
        const agencyIsUT = isUnionTerritory(agencyState)
        const clientIsUT = isUnionTerritory(clientState)

        if (agencyState && clientState && agencyState.toLowerCase() === clientState.toLowerCase()) {
          // Same state - apply CGST + SGST or CGST + UTGST
          if (agencyIsUT && clientIsUT) {
            // Both are Union Territories - apply CGST + UTGST
            cgstAmount = taxableAmount * ((selectedTax.cgst_rate || 0) / 100)
            utgstAmount = taxableAmount * ((selectedTax.utgst_rate || 0) / 100)
            taxAmount = cgstAmount + utgstAmount
            taxDescription = `CGST (${selectedTax.cgst_rate}%) + UTGST (${selectedTax.utgst_rate}%)`
          } else {
            // Same state (regular states) - apply CGST + SGST
            cgstAmount = taxableAmount * ((selectedTax.cgst_rate || 0) / 100)
            sgstAmount = taxableAmount * ((selectedTax.sgst_rate || 0) / 100)
            taxAmount = cgstAmount + sgstAmount
            taxDescription = `CGST (${selectedTax.cgst_rate}%) + SGST (${selectedTax.sgst_rate}%)`
          }
        } else {
          // Different states - apply IGST
          igstAmount = taxableAmount * ((selectedTax.igst_rate || 0) / 100)
          taxAmount = igstAmount
          taxDescription = `IGST (${selectedTax.igst_rate}%)`
        }
      } else {
        // Non-GST tax - apply standard tax rate
        taxAmount = taxableAmount * ((selectedTax.tax_rate || 0) / 100)
        taxDescription = `${selectedTax.tax_name} (${selectedTax.tax_rate}%)`
      }
    }

    // Calculate fees - applied on (Invoice Amount + Tax Amount)
    let feesAmount = 0
    let feesDescription = ''
    const amountForFees = subtotal + taxAmount - discountAmount

    if (selectedFees) {
      if (selectedFees.fees_type === 'PERCENTAGE') {
        feesAmount = amountForFees * ((selectedFees.fees_percentage || 0) / 100)
        feesDescription = `${selectedFees.fees_name} (${selectedFees.fees_percentage}%)`
      } else {
        feesAmount = selectedFees.fees_amount || 0
        feesDescription = `${selectedFees.fees_name} (Fixed)`
      }
    }

    const total = subtotal + taxAmount + feesAmount - discountAmount

    return {
      subtotal,
      taxAmount,
      discountAmount,
      feesAmount,
      total,
      cgstAmount,
      sgstAmount,
      igstAmount,
      utgstAmount,
      isGSTInvoice,
      taxDescription,
      feesDescription,
    }
  }, [formData.line_items, formData.discount_value, selectedTax, selectedFees, agencyConfig?.state, selectedClientDetails?.state])

  const createMutation = useMutation({
    mutationFn: invoicesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast.success(t('invoices.invoiceCreated'))
      navigate('/billing/invoices')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('invoices.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => invoicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      toast.success(t('invoices.invoiceUpdated'))
      navigate(`/billing/invoices/${id}`)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('invoices.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.template_id === templateId)
    setSelectedTemplate(template || null)
    setFormData(prev => ({
      ...prev,
      template_id: templateId,
      notes: template?.default_notes || prev.notes,
      payment_terms: template?.default_payment_terms || prev.payment_terms,
    }))
  }

  const handleClientChange = (clientId: string) => {
    const client = clients.find(c => c.client_id === clientId)
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      business_id: undefined, // Reset business when client changes
      currency: client?.currency || prev.currency,
    }))
  }

  const handleBusinessChange = (businessId: string) => {
    setFormData(prev => ({
      ...prev,
      business_id: businessId === 'none' ? undefined : businessId,
    }))
  }

  const handleTaxChange = (taxId: string) => {
    if (taxId === 'none') {
      setSelectedTax(null)
      setFormData(prev => ({
        ...prev,
        tax_id: undefined,
        tax_rate: 0,
        is_gst_invoice: false,
        cgst_amount: 0,
        sgst_amount: 0,
        igst_amount: 0,
        utgst_amount: 0,
      }))
    } else {
      const tax = taxes.find(t => t.tax_id === taxId)
      setSelectedTax(tax || null)
      setFormData(prev => ({
        ...prev,
        tax_id: taxId,
      }))
    }
  }

  const handleFeesChange = (feesId: string) => {
    if (feesId === 'none') {
      setSelectedFees(null)
      setFormData(prev => ({
        ...prev,
        fees_id: undefined,
        fees_amount: 0,
      }))
    } else {
      const fees = feesList.find(f => f.fees_id === feesId)
      setSelectedFees(fees || null)
      setFormData(prev => ({
        ...prev,
        fees_id: feesId,
      }))
    }
  }

  const handleLineItemChange = (index: number, field: keyof LineItemRequest, value: any) => {
    const newLineItems = [...formData.line_items]
    newLineItems[index] = { ...newLineItems[index], [field]: value }
    setFormData(prev => ({ ...prev, line_items: newLineItems }))
  }

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find(p => p.product_id === productId)
    if (product) {
      const newLineItems = [...formData.line_items]

      newLineItems[index] = {
        ...newLineItems[index],
        product_id: productId,
        description: product.description || product.name,
        unit_price: product.price,
        unit: product.unit,
        tax_rate: product.tax_rate || 0,
      }

      // Build updates for formData
      const formUpdates: Partial<typeof formData> = {
        line_items: newLineItems,
      }

      // If product has tax, set the invoice-level tax
      if (product.tax_id) {
        const tax = taxes.find(t => t.tax_id === product.tax_id)
        if (tax) {
          setSelectedTax(tax)
          formUpdates.tax_id = product.tax_id
          formUpdates.tax_rate = tax.tax_rate
          formUpdates.is_gst_invoice = tax.is_gst
        }
      }

      // If product has discount, set the invoice-level discount
      if (product.discount_type && product.discount_value) {
        formUpdates.discount_type = product.discount_type
        formUpdates.discount_value = product.discount_value
      }

      // If product has fees, set the invoice-level fees
      if (product.fees_id) {
        const fees = feesList.find(f => f.fees_id === product.fees_id)
        if (fees) {
          setSelectedFees(fees)
          formUpdates.fees_id = product.fees_id
        }
      }

      setFormData(prev => ({ ...prev, ...formUpdates }))
    } else if (productId === 'none') {
      // Clear product selection but keep other fields
      const newLineItems = [...formData.line_items]
      newLineItems[index] = {
        ...newLineItems[index],
        product_id: undefined,
      }
      setFormData(prev => ({ ...prev, line_items: newLineItems }))
    }
  }

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0 }],
    }))
  }

  const removeLineItem = (index: number) => {
    if (formData.line_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index),
      }))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Convert date fields to RFC3339 format for the API
    const formatDateToRFC3339 = (dateStr: string | undefined) => {
      if (!dateStr) return undefined
      // If already in ISO format, return as is
      if (dateStr.includes('T')) return dateStr
      // Convert YYYY-MM-DD to RFC3339 (midnight UTC)
      return `${dateStr}T00:00:00Z`
    }

    const apiData = {
      ...formData,
      invoice_date: formatDateToRFC3339(formData.invoice_date),
      due_date: formatDateToRFC3339(formData.due_date)!,
      // Include calculated GST amounts
      tax_rate: selectedTax?.is_gst ? 0 : (selectedTax?.tax_rate || 0),
      is_gst_invoice: calculations.isGSTInvoice,
      cgst_amount: calculations.cgstAmount,
      sgst_amount: calculations.sgstAmount,
      igst_amount: calculations.igstAmount,
      utgst_amount: calculations.utgstAmount,
      // Include fees
      fees_id: selectedFees?.fees_id,
      fees_amount: calculations.feesAmount,
    }

    if (isCreateMode) {
      createMutation.mutate(apiData)
    } else if (isEditMode && id) {
      updateMutation.mutate({ id, data: apiData })
    }
  }

  const isFormValid = () => {
    if (!formData.client_id || !formData.due_date || !formData.currency) return false
    if (formData.line_items.length === 0) return false
    return formData.line_items.every(item => item.description && item.quantity > 0 && item.unit_price >= 0)
  }

  // Open send invoice dialog
  const openSendDialog = () => {
    // Pre-select primary client email if available
    if (availableEmails.length > 0) {
      const primaryEmail = availableEmails.find(e => e.source === 'client')
      if (primaryEmail) {
        setSelectedEmails([primaryEmail.email])
      }
    }
    // Set default subject
    if (existingInvoice) {
      setEmailSubject(`Invoice ${existingInvoice.invoice_number} from ${existingInvoice.template?.company_name || 'Your Company'}`)
    }
    setIsSendDialogOpen(true)
  }

  // Handle email selection toggle
  const toggleEmailSelection = (email: string) => {
    setSelectedEmails(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    )
  }

  // Handle send invoice
  const handleSendInvoice = async () => {
    if (!id || selectedEmails.length === 0) return

    setIsSendingInvoice(true)
    try {
      const result = await invoicesApi.sendInvoice(id, {
        to_emails: selectedEmails,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
      })
      toast.success(result.message || t('invoices.invoiceSent'))
      setIsSendDialogOpen(false)
      setSelectedEmails([])
      setEmailSubject('')
      setEmailMessage('')
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.failedToSend'))
    } finally {
      setIsSendingInvoice(false)
    }
  }

  if (id && isLoadingInvoice) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('invoices.loadingInvoice')}</div>
      </div>
    )
  }

  const pageTitle = isCreateMode ? t('invoices.createInvoice') : (isEditMode ? t('invoices.editInvoice') : t('invoices.invoiceDetails'))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/billing/invoices">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">{pageTitle}</h1>
            {existingInvoice && (
              <p className="text-muted-foreground">{t('invoices.invoiceNumber')} #{existingInvoice.invoice_number}</p>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Invoice Details Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('invoices.invoiceDetails')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('invoices.template')}</Label>
              <Select
                value={formData.template_id || ''}
                onValueChange={handleTemplateChange}
                disabled={isViewMode}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('invoices.selectTemplate')} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.template_id} value={template.template_id}>
                      {template.template_name} {template.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.client')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Select
                value={formData.client_id}
                onValueChange={handleClientChange}
                disabled={isViewMode}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('invoices.selectClient')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.business')}</Label>
              <Select
                value={formData.business_id || 'none'}
                onValueChange={handleBusinessChange}
                disabled={isViewMode || !formData.client_id || !selectedClientDetails?.businesses?.length}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('invoices.selectBusiness')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common.none')}</SelectItem>
                  {selectedClientDetails?.businesses?.map(business => (
                    <SelectItem key={business.business_id} value={business.business_id}>
                      {business.business_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.invoiceNumber')}</Label>
              <Input
                type="text"
                value={isCreateMode ? (formData.invoice_number || '') : (existingInvoice?.invoice_number || '')}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                placeholder={t('common.autoGenerated')}
                disabled={isViewMode || isEditMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.invoiceDate')}</Label>
              <Input
                type="date"
                value={formData.invoice_date || formatDateForInput(new Date().toISOString())}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.dueDate')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                disabled={isViewMode}
                required
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.currency')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Select
                value={formData.currency}
                onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}
                disabled={isViewMode}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="INR">INR</SelectItem>
                  <SelectItem value="AUD">AUD</SelectItem>
                  <SelectItem value="CAD">CAD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Line Items Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('invoices.lineItems')}</h3>
          <Table>
            <TableHeader>
              <TableRow>
                {!isViewMode && <TableHead className="w-[150px] px-2">{t('invoices.product')}</TableHead>}
                <TableHead className="w-[45%] px-2">{t('invoices.description')}</TableHead>
                <TableHead className="px-2">{t('invoices.quantity')}</TableHead>
                <TableHead className="px-2">{t('invoices.unitPrice')}</TableHead>
                <TableHead className="text-right px-2">{t('invoices.amount')}</TableHead>
                {!isViewMode && <TableHead className="w-[40px] px-1"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {formData.line_items.map((item, index) => (
                <TableRow key={index}>
                  {!isViewMode && (
                    <TableCell className="px-2 py-2">
                      <Select
                        value={item.product_id || 'none'}
                        onValueChange={(value) => handleProductSelect(index, value)}
                      >
                        <SelectTrigger className="w-[140px] bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                          <SelectValue placeholder={t('invoices.selectProduct')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('invoices.manualEntry')}</SelectItem>
                          {products.map((product) => (
                            <SelectItem key={product.product_id} value={product.product_id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  )}
                  <TableCell className="px-2 py-2">
                    <Input
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      placeholder={t('invoices.itemDescription')}
                      disabled={isViewMode}
                      className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                    />
                  </TableCell>
                  <TableCell className="px-2 py-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity || ''}
                      onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20 bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                      disabled={isViewMode}
                    />
                  </TableCell>
                  <TableCell className="px-2 py-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price || ''}
                      onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-24 bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                      disabled={isViewMode}
                    />
                  </TableCell>
                  <TableCell className="text-right font-medium px-2 py-2">
                    {formatCurrency(item.quantity * item.unit_price, formData.currency)}
                  </TableCell>
                  {!isViewMode && (
                    <TableCell className="px-1 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        disabled={formData.line_items.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!isViewMode && (
            <Button type="button" variant="outline" className="mt-4" onClick={addLineItem}>
              <Plus className="mr-2 h-4 w-4" />
              {t('invoices.addLineItem')}
            </Button>
          )}
        </div>

        {/* Tax & Fees Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('invoices.taxAndFees')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">{t('invoices.selectTax')}</Label>
              <Select
                value={formData.tax_id || 'none'}
                onValueChange={handleTaxChange}
                disabled={isViewMode}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('invoices.selectTax')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('invoices.noTax')}</SelectItem>
                  {taxes.map(tax => (
                    <SelectItem key={tax.tax_id} value={tax.tax_id}>
                      {tax.tax_name} {tax.is_gst ? '(GST)' : `(${tax.tax_rate}%)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">{t('invoices.selectFees')}</Label>
              <Select
                value={formData.fees_id || 'none'}
                onValueChange={handleFeesChange}
                disabled={isViewMode}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('invoices.selectFees')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('invoices.noFees')}</SelectItem>
                  {feesList.map(fees => (
                    <SelectItem key={fees.fees_id} value={fees.fees_id}>
                      {fees.fees_name} {fees.fees_type === 'PERCENTAGE' ? `(${fees.fees_percentage}%)` : `(₹${fees.fees_amount})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">{t('invoices.discount')}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.discount_value || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
          </div>
        </div>

        {/* Notes & Terms Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('invoices.discountNotes')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('invoices.notes')}</Label>
              <Textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={t('invoices.notesPlaceholder')}
                rows={3}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.paymentTerms')}</Label>
              <Textarea
                value={formData.payment_terms || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                placeholder={t('invoices.paymentTermsPlaceholder')}
                rows={3}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
          </div>
        </div>

        {/* Summary & Live Preview Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('invoices.summary')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Summary Card */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('invoices.subtotal')}</span>
                  <span>{formatCurrency(calculations.subtotal, formData.currency)}</span>
                </div>

                {calculations.discountAmount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>{t('invoices.discount')}</span>
                    <span>-{formatCurrency(calculations.discountAmount, formData.currency)}</span>
                  </div>
                )}

                {/* Tax Breakdown */}
                {selectedTax && (
                  <div className="space-y-2 text-sm">
                    {calculations.isGSTInvoice ? (
                      <>
                        {calculations.cgstAmount > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>CGST ({selectedTax.cgst_rate}%)</span>
                            <span>{formatCurrency(calculations.cgstAmount, formData.currency)}</span>
                          </div>
                        )}
                        {calculations.sgstAmount > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>SGST ({selectedTax.sgst_rate}%)</span>
                            <span>{formatCurrency(calculations.sgstAmount, formData.currency)}</span>
                          </div>
                        )}
                        {calculations.igstAmount > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>IGST ({selectedTax.igst_rate}%)</span>
                            <span>{formatCurrency(calculations.igstAmount, formData.currency)}</span>
                          </div>
                        )}
                        {calculations.utgstAmount > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>UTGST ({selectedTax.utgst_rate}%)</span>
                            <span>{formatCurrency(calculations.utgstAmount, formData.currency)}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex justify-between text-muted-foreground">
                        <span>{selectedTax.tax_name} ({selectedTax.tax_rate}%)</span>
                        <span>{formatCurrency(calculations.taxAmount, formData.currency)}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('invoices.taxTotal')}</span>
                  <span>{formatCurrency(calculations.taxAmount, formData.currency)}</span>
                </div>

                {/* Fees Breakdown */}
                {selectedFees && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>
                        {selectedFees.fees_name}
                        {selectedFees.fees_type === 'PERCENTAGE'
                          ? ` (${selectedFees.fees_percentage}% ${t('invoices.onInvoicePlusTax')})`
                          : ` (${t('invoices.fixed')})`}
                      </span>
                      <span>{formatCurrency(calculations.feesAmount, formData.currency)}</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('invoices.feesTotal')}</span>
                  <span>{formatCurrency(calculations.feesAmount, formData.currency)}</span>
                </div>

                <Separator />

                <div className="flex justify-between text-lg font-bold">
                  <span>{t('invoices.total')}</span>
                  <span>{formatCurrency(calculations.total, formData.currency)}</span>
                </div>

                {/* Currency Conversion Info - Show in view mode when invoice has conversion data */}
                {isViewMode && existingInvoice?.exchange_rate && existingInvoice?.amount_in_base_currency && existingInvoice?.base_currency && existingInvoice.currency !== existingInvoice.base_currency && (
                  <>
                    <Separator />
                    <div className="space-y-2 text-sm bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 font-medium">
                        <span>{t('invoices.currencyConversion', 'Currency Conversion')}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>{t('invoices.exchangeRate', 'Exchange Rate')}</span>
                        <span>1 {existingInvoice.currency} = {existingInvoice.exchange_rate.toFixed(4)} {existingInvoice.base_currency}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>{t('invoices.amountInBaseCurrency', 'Amount in {{currency}}').replace('{{currency}}', existingInvoice.base_currency)}</span>
                        <span>{formatCurrency(existingInvoice.amount_in_base_currency, existingInvoice.base_currency)}</span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Live Preview Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="h-4 w-4 text-primary" />
                  <span className="font-medium">{t('invoices.livePreview')}</span>
                </div>
                <div className="border rounded-lg p-4 bg-white dark:bg-zinc-950 text-sm">
                  {/* Mini Invoice Preview */}
                  <div className="space-y-3">
                    {selectedTemplate && (
                      <div className="text-center border-b pb-2">
                        <h3 className="font-bold">{selectedTemplate.company_name || t('invoices.yourCompany')}</h3>
                        {selectedTemplate.company_address_line1 && (
                          <p className="text-xs text-muted-foreground">{selectedTemplate.company_address_line1}</p>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between text-xs">
                      <div>
                        <p className="font-medium">{t('invoices.billTo')}:</p>
                        <p>{selectedClient?.client_name || t('invoices.selectClient')}</p>
                        {selectedClient?.client_email && <p>{selectedClient.client_email}</p>}
                      </div>
                      <div className="text-right">
                        <p><span className="font-medium">{t('invoices.date')}:</span> {formData.invoice_date || t('common.today')}</p>
                        <p><span className="font-medium">{t('invoices.due')}:</span> {formData.due_date}</p>
                      </div>
                    </div>

                    <div className="border-t pt-2">
                      {formData.line_items.slice(0, 3).map((item, i) => (
                        <div key={i} className="flex justify-between text-xs py-1">
                          <span className="truncate max-w-[60%]">{item.description || t('invoices.item')}</span>
                          <span>{formatCurrency(item.quantity * item.unit_price, formData.currency)}</span>
                        </div>
                      ))}
                      {formData.line_items.length > 3 && (
                        <p className="text-xs text-muted-foreground">+{formData.line_items.length - 3} {t('invoices.moreItems')}</p>
                      )}
                    </div>

                    <div className="border-t pt-2 text-right">
                      <p className="font-bold">{t('invoices.total')}: {formatCurrency(calculations.total, formData.currency)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Actions */}
        {!isViewMode && (
          <div className="flex justify-center gap-4 pt-4">
            <Button
              type="submit"
              disabled={!isFormValid() || createMutation.isPending || updateMutation.isPending}
              onClick={handleSubmit}
            >
              {createMutation.isPending || updateMutation.isPending
                ? t('common.saving')
                : isCreateMode
                  ? t('invoices.createInvoice')
                  : t('common.saveChanges')}
            </Button>
            <Link to="/billing/invoices">
              <Button type="button" variant="outline">
                {t('common.cancel')}
              </Button>
            </Link>
          </div>
        )}

        {/* Action Buttons - only show in view mode */}
        {isViewMode && id && (
          <div className="flex justify-center gap-4 pt-4">
            <Button
              type="button"
              onClick={openSendDialog}
            >
              <Mail className="mr-2 h-4 w-4" />
              {t('invoices.sendInvoice')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPaymentLinkDialogOpen(true)}
              disabled={existingInvoice?.status === 'PAID'}
            >
              <Link2 className="mr-2 h-4 w-4" />
              {t('payments.paymentLink', 'Payment Link')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isDownloadingPdf}
              onClick={async () => {
                setIsDownloadingPdf(true)
                try {
                  const blob = await invoicesApi.downloadPdf(id)
                  const url = window.URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `invoice_${existingInvoice?.invoice_number || id}.pdf`
                  document.body.appendChild(a)
                  a.click()
                  window.URL.revokeObjectURL(url)
                  document.body.removeChild(a)
                  toast.success(t('invoices.pdfDownloaded'))
                } catch (error: any) {
                  console.error('Failed to download PDF:', error)
                  const errorMessage = error.response?.data?.message || error.message || t('invoices.failedToDownloadPdf')
                  toast.error(errorMessage)
                } finally {
                  setIsDownloadingPdf(false)
                }
              }}
            >
              {isDownloadingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {t('invoices.downloadPdf')}
            </Button>
          </div>
        )}
      </form>

      {/* Send Invoice Dialog */}
      <Dialog open={isSendDialogOpen} onOpenChange={setIsSendDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {t('invoices.sendInvoice')}
            </DialogTitle>
            <DialogDescription>
              {t('invoices.selectRecipientsMessage')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Email Recipients */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('invoices.sendTo')}</Label>
              {availableEmails.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                  {availableEmails.map((emailInfo, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                      onClick={() => toggleEmailSelection(emailInfo.email)}
                    >
                      <Checkbox
                        checked={selectedEmails.includes(emailInfo.email)}
                        onCheckedChange={() => toggleEmailSelection(emailInfo.email)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{emailInfo.email}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{emailInfo.label}</span>
                          <Badge variant="outline" className="text-xs">
                            {emailInfo.source === 'client' ? t('clients.client') :
                             emailInfo.source === 'business' ? t('clients.business') : t('clients.contact')}
                          </Badge>
                        </div>
                      </div>
                      {selectedEmails.includes(emailInfo.email) && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('invoices.noEmailsFound')}
                </p>
              )}
              {selectedEmails.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedEmails.length} {t('invoices.recipientsSelected')}
                </p>
              )}
            </div>

            {/* Email Subject */}
            <div className="space-y-2">
              <Label htmlFor="email-subject">{t('invoices.subjectOptional')}</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder={`${t('invoices.invoice')} ${existingInvoice?.invoice_number || ''}`}
              />
            </div>

            {/* Email Message */}
            <div className="space-y-2">
              <Label htmlFor="email-message">{t('invoices.messageOptional')}</Label>
              <Textarea
                id="email-message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder={t('invoices.messagePlaceholder')}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {t('invoices.leaveBlankDefault')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSendDialogOpen(false)}
              disabled={isSendingInvoice}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSendInvoice}
              disabled={selectedEmails.length === 0 || isSendingInvoice}
            >
              {isSendingInvoice ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('invoices.sending')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('invoices.sendInvoice')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Link Dialog */}
      {existingInvoice && (
        <PaymentLinkDialog
          open={isPaymentLinkDialogOpen}
          onOpenChange={setIsPaymentLinkDialogOpen}
          invoiceId={id!}
          invoiceNumber={existingInvoice.invoice_number}
          balanceDue={existingInvoice.balance_due}
          currency={existingInvoice.currency}
        />
      )}
    </div>
  )
}

